import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI, profileAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  MousePointerClick,
  Eye,
  UserPlus,
  Video,
  Share2,
  TrendingUp,
  Calendar,
  ShoppingBag,
  ClipboardList
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PROFILE_COLORS = {
  green: '#22C55E',
  blue: '#3B82F6',
  red: '#EF4444',
  yellow: '#EAB308',
  purple: '#A855F7',
  black: '#171717',
};

const AnalyticsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileAnalytics, setProfileAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, profilesRes] = await Promise.all([
          analyticsAPI.get(),
          profileAPI.list(),
        ]);
        setAnalytics(analyticsRes.data);
        setProfiles(profilesRes.data);

        // Fetch analytics for each profile
        const profileStats = await Promise.all(
          profilesRes.data.map(async (profile) => {
            try {
              const res = await analyticsAPI.getProfile(profile.profile_id);
              return { ...res.data, profile_name: profile.profile_name, color: profile.color };
            } catch {
              return { profile_id: profile.profile_id, profile_name: profile.profile_name, color: profile.color, total_taps: 0, unique_visitors: 0, leads_count: 0 };
            }
          })
        );
        setProfileAnalytics(profileStats);
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics', active: true },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  const stats = analytics ? [
    { label: 'Total Taps', value: analytics.total_taps, icon: <MousePointerClick className="w-5 h-5" />, color: 'text-indigo-600 bg-indigo-100' },
    { label: 'Profile Views', value: analytics.unique_visitors, icon: <Eye className="w-5 h-5" />, color: 'text-emerald-600 bg-emerald-100' },
    { label: 'Leads Captured', value: analytics.leads_count, icon: <UserPlus className="w-5 h-5" />, color: 'text-amber-600 bg-amber-100' },
    { label: 'Video Plays', value: analytics.video_plays, icon: <Video className="w-5 h-5" />, color: 'text-purple-600 bg-purple-100' },
    { label: 'Contact Saves', value: analytics.contact_saves, icon: <TrendingUp className="w-5 h-5" />, color: 'text-blue-600 bg-blue-100' },
    { label: 'Forward Shares', value: analytics.forward_shares, icon: <Share2 className="w-5 h-5" />, color: 'text-pink-600 bg-pink-100' },
  ] : [];

  const chartData = profileAnalytics.map(p => ({
    name: p.profile_name,
    views: p.unique_visitors,
    leads: p.leads_count,
    taps: p.total_taps,
    fill: PROFILE_COLORS[p.color] || '#4F46E5'
  }));

  const pieData = profileAnalytics.filter(p => p.unique_visitors > 0).map(p => ({
    name: p.profile_name,
    value: p.unique_visitors,
    fill: PROFILE_COLORS[p.color] || '#4F46E5'
  }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Analytics</h1>
            <NotificationBell />
          </div>
        </header>

        <main className="p-6" data-testid="analytics-page">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color} mb-3`}>
                  {stat.icon}
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <div className="chart-container">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Profile Performance
              </h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Bar dataKey="views" name="Views" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="leads" name="Leads" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No data yet
                </div>
              )}
            </div>

            {/* Pie Chart */}
            <div className="chart-container">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Views by Profile
              </h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No data yet
                </div>
              )}
            </div>
          </div>

          {/* Profile Stats Table */}
          <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Profile Details
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Profile</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Taps</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Views</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Leads</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Contact Saves</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {profileAnalytics.map((profile) => (
                    <tr key={profile.profile_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-lg"
                            style={{ backgroundColor: PROFILE_COLORS[profile.color] || '#4F46E5' }}
                          />
                          <span className="font-medium text-slate-900">{profile.profile_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-slate-600">{profile.total_taps}</td>
                      <td className="px-6 py-4 text-right text-slate-600">{profile.unique_visitors}</td>
                      <td className="px-6 py-4 text-right text-slate-600">{profile.leads_count}</td>
                      <td className="px-6 py-4 text-right text-slate-600">{profile.contact_saves || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AnalyticsPage;
